import { useState, useRef, useEffect } from "react";
import { api } from "../lib/api";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Loader2, Check, X, Search } from "lucide-react";

// Selettore partner con autocomplete: mostra i risultati solo dopo 4 lettere (anti-fake).
export function PartnerPicker({ value, onChange, label }) {
  const [via, setVia] = useState(value?.via_partner || false);
  const [q, setQ] = useState(value?.partner_name || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(value?.partner_id ? { id: value.partner_id, name: value.partner_name, logo_url: value.partner_logo_url } : null);
  const tRef = useRef(null);
  const emit = (v) => onChange && onChange(v);

  // Arrivo dal sito torri.solar con ?partner_id=<id rete> o ?partner=<slug>: partner preselezionato.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const pid = qs.get("partner_id") || "", slug = qs.get("partner") || "";
    if ((!pid && !slug) || value?.partner_id) return;
    api.get("/public/partners/resolve", { params: { partner_id: pid, partner: slug } }).then(({ data }) => {
      setVia(true); setSelected(data); setQ(data.name);
      emit({ via_partner: true, partner_id: data.id, partner_name: data.name, partner_logo_url: data.logo_url || "" });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (v) => {
    setVia(v);
    if (!v) { setQ(""); setResults([]); setSelected(null); emit({ via_partner: false, partner_id: "", partner_name: "", partner_logo_url: "" }); }
    else emit({ via_partner: true, partner_id: "", partner_name: "", partner_logo_url: "" });
  };

  const onType = (val) => {
    setQ(val); setSelected(null);
    emit({ via_partner: true, partner_id: "", partner_name: val, partner_logo_url: "" });
    clearTimeout(tRef.current);
    if (val.trim().length < 4) { setResults([]); return; }
    tRef.current = setTimeout(async () => {
      setLoading(true);
      try { const { data } = await api.get("/public/partners/search", { params: { q: val.trim() } }); setResults(data.items || []); }
      catch { setResults([]); }
      finally { setLoading(false); }
    }, 300);
  };

  const pick = (r) => { setSelected(r); setQ(r.name); setResults([]); emit({ via_partner: true, partner_id: r.id, partner_name: r.name, partner_logo_url: r.logo_url || "" }); };
  const clear = () => { setSelected(null); setQ(""); setResults([]); emit({ via_partner: true, partner_id: "", partner_name: "", partner_logo_url: "" }); };

  return (
    <div className="space-y-2" data-testid="partner-picker">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label || "Sei stato indirizzato da un partner (o ne conosci uno)?"}</Label>
      <div className="flex gap-2">
        <button type="button" onClick={() => toggle(true)} data-testid="partner-yes" className={`px-4 py-1.5 rounded border text-sm font-medium ${via ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>Sì</button>
        <button type="button" onClick={() => toggle(false)} data-testid="partner-no" className={`px-4 py-1.5 rounded border text-sm font-medium ${!via ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground"}`}>No</button>
      </div>
      {via && (
        <div className="relative">
          <div className="relative flex items-center">
            <Search className="h-4 w-4 text-muted-foreground absolute left-3" />
            <Input value={q} onChange={(e) => onType(e.target.value)} placeholder="Scrivi almeno 4 lettere del partner…" className="pl-9" data-testid="partner-search-input" autoComplete="off" />
            {loading && <Loader2 className="h-4 w-4 animate-spin absolute right-3 text-muted-foreground" />}
          </div>
          {q.trim().length > 0 && q.trim().length < 4 && !selected && <p className="text-[11px] text-muted-foreground mt-1">Scrivi almeno 4 lettere per cercare il partner.</p>}
          {results.length > 0 && !selected && (
            <div className="absolute z-30 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg max-h-56 overflow-auto" data-testid="partner-results">
              {results.map((r) => (
                <button type="button" key={r.id} onClick={() => pick(r)} className="flex items-center gap-2 w-full text-left px-3 py-2 hover:bg-secondary/50 text-sm" data-testid={`partner-opt-${r.id}`}>
                  {r.logo_url ? <img src={r.logo_url} alt="" className="h-6 w-6 rounded object-contain bg-white border border-border" /> : <span className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">{(r.name || "?").slice(0, 2).toUpperCase()}</span>}
                  <span className="truncate">{r.name}</span>
                  <span className="ml-auto text-[11px] text-muted-foreground whitespace-nowrap">{[r.comune, r.provincia].filter(Boolean).join(" · ")}</span>
                </button>
              ))}
            </div>
          )}
          {selected && (
            <div className="mt-2 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-3 py-2 text-sm" data-testid="partner-selected">
              {selected.logo_url ? <img src={selected.logo_url} alt="" className="h-7 w-7 rounded object-contain bg-white border border-border" /> : <Check className="h-4 w-4 text-primary" />}
              <span className="font-medium">{selected.name}</span>
              <button type="button" onClick={clear} className="ml-auto text-muted-foreground hover:text-destructive" data-testid="partner-clear"><X className="h-4 w-4" /></button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
